import axios from "axios";
import { PropsWithChildren, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { bindActionCreators } from "redux";
import AppComponent from "./App";
import setupAxiosInterceptors from "./config/axios-interceptor";
import getStore, { useAppSelector } from "./config/store";
import { registerLocale } from "./config/translation";
import { socket, SocketContext } from "./context/socket";
import { updateThemeColors } from "./design-system/colors";
import { applyThemeMode, initializeTheme } from "./design-system/theme";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { IEdoSetting } from "./shared/model/edo-setting.model.ts";
import { EdoSettingEnum } from "./shared/model/enumerations/edo-setting.model.ts";
import { clearAuthentication } from "./shared/reducers/authentication.ts";
import { SettingsState } from "./shared/reducers/settings.reducer.ts";
import { THEME_MODE_KEY } from "./shared/util/global-settings.ts";

const store = getStore();
registerLocale(store);

initializeTheme();

const actions = bindActionCreators({ clearAuthentication }, store.dispatch);

setupAxiosInterceptors(() =>
  actions.clearAuthentication("login.error.unauthorized")
);

const container = document.getElementById("root");

const ThemeWrapper = (props: PropsWithChildren) => {
  const [settings, setSettings] = useState<{
    primaryColor: string | null;
    secondaryColor: string | null;
  }>({ primaryColor: null, secondaryColor: null });
  const { theme: themeMode } = useAppSelector<SettingsState>(
    (state) => state.settings
  );

  const getSetting = async (key: string): Promise<IEdoSetting> => {
    const response = await axios.get<{ data: IEdoSetting }>(`/settings/${key}`);
    return response.data.data;
  };

  const getSettings = async () => {
    const [primaryColorSetting, secondaryColorSetting] = await Promise.allSettled([
      getSetting(EdoSettingEnum.THEME_PRIMARY_COLOR),
      getSetting(EdoSettingEnum.THEME_SECONDARY_COLOR),
    ]);

    const primaryColorValue: string | null =
      primaryColorSetting.status === "fulfilled"
        ? primaryColorSetting.value.value?.value || null
        : null;
    const secondaryColorValue: string | null =
      secondaryColorSetting.status === "fulfilled"
        ? secondaryColorSetting.value.value?.value || null
        : null;

    setSettings({
      primaryColor: primaryColorValue,
      secondaryColor: secondaryColorValue,
    });

    if (primaryColorValue !== null) {
      localStorage.setItem(EdoSettingEnum.THEME_PRIMARY_COLOR, primaryColorValue);
    } else {
      localStorage.removeItem(EdoSettingEnum.THEME_PRIMARY_COLOR);
    }

    if (secondaryColorValue !== null) {
      localStorage.setItem(
        EdoSettingEnum.THEME_SECONDARY_COLOR,
        secondaryColorValue
      );
    } else {
      localStorage.removeItem(EdoSettingEnum.THEME_SECONDARY_COLOR);
    }
  };

  useEffect(() => {
    getSettings();
  }, []);

  useEffect(() => {
    if (themeMode === "dark" || themeMode === "light") {
      localStorage.setItem(THEME_MODE_KEY, String(themeMode));
      applyThemeMode(themeMode);
    }
  }, [themeMode]);

  useEffect(() => {
    updateThemeColors({
      primary:
        settings.primaryColor ||
        localStorage.getItem(EdoSettingEnum.THEME_PRIMARY_COLOR) ||
        "#2196f3",
      secondary:
        settings.secondaryColor ||
        localStorage.getItem(EdoSettingEnum.THEME_SECONDARY_COLOR) ||
        "#f50057",
    });
  }, [settings.primaryColor, settings.secondaryColor]);

  return <>{props.children}</>;
};

const root = createRoot(container as HTMLElement);

root.render(
  <>
    <Provider store={store}>
      <SocketContext.Provider value={socket}>
        <ThemeWrapper>
          <AppComponent />
        </ThemeWrapper>
      </SocketContext.Provider>
    </Provider>
  </>
);

serviceWorkerRegistration.register();
reportWebVitals();
